import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { scrollToSection } from "@/lib/lenis";

const HERO_IMG = "/images/hero.jpg";

const lines = [
  { text: "HIGH-GRADE", cls: "text-ink" },
  { text: "CHROME,", cls: "text-ink" },
  { text: "PERFECTED.", cls: "text-chrome" },
];

const stats = [
  { value: "40–42%+", label: "Cr2O3 CONCENTRATE & HIGHER GRADES", testid: "hero-stat-grade" },
  { value: "+15,000", label: "TONS / MONTH, SCALING", testid: "hero-stat-tonnage" },
  { value: "GLOBAL", label: "DELIVERY, ALL CLIENTS", testid: "hero-stat-global" },
];

export const Hero = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const imgY = useTransform(scrollYProgress, [0, 1], ["0%", "18%"]);

  return (
    <section id="top" ref={ref} data-testid="hero-section" className="relative pt-28 md:pt-40 pb-16 md:pb-24 overflow-hidden">
      <div className="px-6 md:px-12 lg:px-24 grid lg:grid-cols-12 gap-12 lg:gap-8 items-end">
        <div className="lg:col-span-7 relative z-10">
          <motion.p
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.1 }}
            className="font-mono text-[11px] uppercase tracking-[0.25em] text-chrome mb-8"
            data-testid="hero-kicker"
          >
            Obrigado PH Holdings — Mining Prodigies — South Africa
          </motion.p>
          <h1 data-testid="hero-heading" className="font-display font-semibold tracking-tighter leading-[0.9] text-5xl sm:text-6xl md:text-7xl lg:text-[5.5rem]">
            {lines.map((line, i) => (
              <span key={line.text} className="block overflow-hidden pb-1">
                <motion.span
                  className={`block ${line.cls}`}
                  initial={{ y: "110%" }}
                  animate={{ y: 0 }}
                  transition={{ duration: 0.9, delay: 0.25 + i * 0.14, ease: [0.22, 1, 0.36, 1] }}
                >
                  {line.text}
                </motion.span>
              </span>
            ))}
          </h1>
          <motion.p
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.85 }}
            className="mt-10 max-w-xl text-base md:text-lg leading-relaxed text-slate-600"
            data-testid="hero-subtext"
          >
            We source, beneficiate and distribute premium chrome concentrate — from
            40–42% Cr2O3 to higher grades — the feedstock behind the world's stainless
            steel. From South Africa's richest corridors to clients on every continent.
          </motion.p>
          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 1 }}
            className="mt-10 flex flex-wrap gap-4"
          >
            <button
              data-testid="hero-explore-button"
              onClick={() => scrollToSection("#product")}
              className="bg-chrome text-white font-mono text-[11px] uppercase tracking-[0.2em] px-7 py-4 hover:bg-ink transition-colors duration-300"
            >
              Explore the Product
            </button>
            <button
              data-testid="hero-contact-button"
              onClick={() => scrollToSection("#contact")}
              className="border border-ink/20 text-ink font-mono text-[11px] uppercase tracking-[0.2em] px-7 py-4 hover:border-chrome hover:text-chrome transition-colors duration-300"
            >
              Talk to Us
            </button>
          </motion.div>
        </div>
        <div className="lg:col-span-5 relative">
          <div className="absolute -top-4 -right-4 w-full h-full border border-chrome/30 pointer-events-none" />
          <motion.div
            initial={{ opacity: 0, scale: 1.04 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 1.2, delay: 0.5, ease: [0.22, 1, 0.36, 1] }}
            className="relative overflow-hidden aspect-[4/5] max-h-[70vh]"
          >
            <motion.img
              src={HERO_IMG}
              alt="Chrome mining operation in South Africa"
              data-testid="hero-image"
              style={{ y: imgY }}
              className="spotlight w-full h-[120%] object-cover"
            />
          </motion.div>
        </div>
      </div>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.8, delay: 1.2 }}
        className="px-6 md:px-12 lg:px-24 mt-16 md:mt-24 grid grid-cols-1 sm:grid-cols-3 border-t border-ink/10"
        data-testid="hero-stats"
      >
        {stats.map((s) => (
          <div key={s.label} data-testid={s.testid} className="py-8 sm:py-10 sm:border-l first:border-l-0 border-ink/10 sm:pl-8 first:pl-0">
            <div className="font-display font-semibold text-3xl md:text-4xl tracking-tight text-ink">{s.value}</div>
            <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-slate-500 mt-2">{s.label}</div>
          </div>
        ))}
      </motion.div>
    </section>
  );
};
